import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getTitleFromPath } from '@/utils/routeTitles';
import { versaoEmUmaLinha } from '@/utils/versao';
import { BotaoDeRelato } from '@/features/relatos/components/RelatarAlgo';
import { podeRelatarNesteMomento } from '@/features/relatos/podeRelatar';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * O LIMITE DE ERRO GLOBAL — a tela branca passa a contar alguma coisa
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Não existia. O único limite de erro do portal era o `LiveOverlayErrorBoundary`,
 * preso ao overlay do scanner. Qualquer outra tela que quebrasse deixava o
 * professor com TELA BRANCA — sem texto, sem botão, sem nada que ele pudesse
 * repetir para mim.
 *
 * Isso custa duas vezes: ele não sabe o que fazer, e eu não fico sabendo que
 * aconteceu. "O portal não abre" é o que chega, e não dá para investigar.
 *
 * ─── POR QUE ELE VALE MESMO SEM O CANAL DE RELATO ──────────────────────────
 *
 * Enquanto não há canal, o professor manda mensagem no WhatsApp. A diferença
 * entre uma foto de tela branca e uma foto DESTA tela é a diferença entre um
 * relato inútil e um relato investigável: aqui estão o nome da tela, a rota, a
 * versão e a mensagem do erro.
 *
 * ─── O CASO QUE ELE TRATA À PARTE, E POR QUÊ ───────────────────────────────
 *
 * A falha mais provável logo depois de uma publicação é o `import()` de um
 * pedaço que a publicação nova renomeou. Ela é diferente de todas as outras em
 * uma coisa: RECARREGAR RESOLVE. Tratá-la como erro genérico faria o professor
 * relatar um defeito que não existe mais, e esperar por uma resposta que só
 * poderia dizer "recarregue".
 *
 * A guarda de uma geração (`dist-anterior/`) já reduz muito esses casos, mas
 * não a zero: duas publicações na mesma sessão aberta ainda chegam aqui.
 *
 * ─── A CHAVE PELA ROTA ─────────────────────────────────────────────────────
 *
 * `key={pathname}` no limite: navegar RECONSTRÓI o componente e limpa o erro.
 * Sem isso, um limite que capturou uma vez fica preso para sempre, e "Voltar ao
 * início" levaria à mesma tela de erro — o botão existiria e não funcionaria.
 */

/**
 * Há sessão? Lido do `localStorage` e não do contexto: este limite fica POR FORA
 * do `AuthProvider` de propósito, para alcançar uma falha do próprio provedor.
 */
function temSessao(): boolean {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
}

/** Erros de carregamento de módulo. Recarregar resolve; relatar, não. */
function ehModuloQueSumiu(erro: Error | null): boolean {
  const m = `${erro?.message ?? ''} ${erro?.name ?? ''}`;
  return (
    /dynamically imported module/i.test(m) ||
    /Importing a module script failed/i.test(m) ||
    /error loading dynamically imported module/i.test(m) ||
    (/ChunkLoadError/i.test(m))
  );
}

interface Props {
  children: ReactNode;
  rota: string;
}

interface State {
  erro: Error | null;
  pilha: string | null;
}

class LimiteDeErro extends Component<Props, State> {
  state: State = { erro: null, pilha: null };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo): void {
    // Fica no console para quem tiver o devtools aberto. Não é o canal — é o
    // que salva a investigação quando alguém consegue abrir o inspetor.
    console.error('[ErroNaTela]', this.props.rota, erro, info.componentStack);
    this.setState({ pilha: info.componentStack ?? null });
  }

  private tentarDeNovo = () => this.setState({ erro: null, pilha: null });

  render(): ReactNode {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    const tela = getTitleFromPath(this.props.rota);
    const desatualizado = ehModuloQueSumiu(erro);

    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col justify-center px-4 py-10">
        {desatualizado ? (
          <>
            <h1 className="text-xl font-semibold text-tinta">O portal foi atualizado</h1>
            <p className="mt-2 text-sm text-gray-700">
              Saiu uma versão nova enquanto esta aba estava aberta, e esta tela ficou
              com pedaços da versão anterior. Recarregar resolve — nada do que você
              fez se perde.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 self-start rounded-md bg-marca-600 px-4 py-2 text-sm font-medium text-white hover:bg-marca-700"
            >
              Recarregar o portal
            </button>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-tinta">Algo quebrou nesta tela</h1>
            <p className="mt-2 text-sm text-gray-700">
              O erro ficou contido aqui — o resto do portal continua funcionando, e
              nada que você já salvou foi perdido.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={this.tentarDeNovo}
                className="rounded-md bg-marca-600 px-4 py-2 text-sm font-medium text-white hover:bg-marca-700"
              >
                Tentar de novo
              </button>
              {/*
                O CANAL EXATAMENTE ONDE ELE FALTAVA.
                A objeção ao item de menu era esta: menu é discreto e some na
                hora em que a pessoa precisa — que é justamente quando algo
                quebrou. Aqui não some, e chega com o erro já anexado, sem a
                pessoa ter de descrever o que não sabe descrever.
                Só com sessão: sem token a rota devolveria 401, e um botão que
                não funciona é pior que botão nenhum.
              */}
              {temSessao() && podeRelatarNesteMomento(this.props.rota) && (
                <BotaoDeRelato
                  className="flex items-center gap-1.5 rounded-md border border-amber-400 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100"
                  rotulo="Relatar isto"
                  erro={erro.message || erro.name}
                />
              )}
              <Link
                to="/"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Voltar ao início
              </Link>
            </div>
          </>
        )}

        {/*
          O DETALHE TÉCNICO FICA VISÍVEL, e não escondido atrás de um "avançado".
          Enquanto não há canal de relato, o que o professor faz é fotografar a
          tela e mandar. Uma foto que traz tela, rota, versão e mensagem é um
          relato; uma foto de "algo deu errado" não é.
        */}
        <div className="mt-8 rounded-md border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Detalhe técnico
          </p>
          <dl className="mt-2 space-y-1 text-xs text-gray-700">
            <div><dt className="inline font-medium">Tela: </dt><dd className="inline">{tela}</dd></div>
            <div><dt className="inline font-medium">Rota: </dt><dd className="inline font-mono">{this.props.rota}</dd></div>
            <div><dt className="inline font-medium">Versão: </dt><dd className="inline font-mono">{versaoEmUmaLinha()}</dd></div>
            <div><dt className="inline font-medium">Erro: </dt><dd className="inline font-mono break-words">{erro.message || erro.name}</dd></div>
          </dl>
        </div>
      </div>
    );
  }
}

/**
 * O invólucro que dá a rota ao limite e o RECONSTRÓI a cada navegação.
 * Limite de erro tem de ser classe (só classe implementa `componentDidCatch`),
 * e classe não lê hook — daí os dois componentes.
 */
export function LimiteDeErroGlobal({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <LimiteDeErro key={pathname} rota={pathname}>
      {children}
    </LimiteDeErro>
  );
}
